/**
 * useKnownRepos — fetches the `known_repos` context key for
 * specValidator.ts's classifyRepo() (track:build-a-spec-cross-repo-detection,
 * seq #42013). Mirrors useActiveProject.ts's GET /api/workspace/context/:key
 * pattern exactly (same auth header + response envelope).
 *
 * Travis seeds and maintains known_repos — a missing key is a normal state
 * (not yet seeded), not an error: callers get `null` and specValidator's
 * classifyRepo-based checks simply stay silent (no cross-repo detection)
 * until it exists.
 *
 * `enabled` gates the fetch — pass a modal's `open` prop so it fetches once
 * on first open and caches for the rest of the session (spec's requirement
 * for the Build-A-Spec modal specifically); omit it (defaults to true) for
 * an always-mounted consumer like BacklogTab that just wants it at init.
 */
import { useEffect, useRef, useState } from "react";
import { getAuthHeader } from "../../lib/auth";
import type { KnownRepos } from "../../lib/specValidator";

const KNOWN_REPOS_KEY = "known_repos";

function parseKnownRepos(raw: string | undefined): KnownRepos | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as KnownRepos) : null;
  } catch {
    return null;
  }
}

export function useKnownRepos(enabled: boolean = true): KnownRepos | null {
  const [knownRepos, setKnownRepos] = useState<KnownRepos | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!enabled || fetchedRef.current) return;
    fetchedRef.current = true;
    let cancelled = false;
    getAuthHeader()
      .then(authHeaders => fetch(`/api/workspace/context/${KNOWN_REPOS_KEY}`, { headers: authHeaders }))
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (cancelled) return;
        setKnownRepos(parseKnownRepos(data?.value));
      })
      .catch(err => console.warn("[specValidator] get_context known_repos failed", err));
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return knownRepos;
}
